import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import rewardIcon from "assets/icons/reward-icon.png";
import ActionBtn from "components/buttons/ActionBtn";
import EmptyState from "components/EmptyState/EmptyState";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Options } from "components/table/components/Options/Options";
import { getFieldErrorKeys } from "helpers/getFieldErrorKeys";
import { handleApiError } from "helpers/handleApiError";
import {
  getActiveCashbackSelectorItems,
  getLoyaltyBonusSelectorItems,
  LoyaltyRewardSelectorItem,
} from "services/loyaltyRewardsService";
import { useAppDispatch, useAppSelector } from "store";
import { handleUpdateFields } from "store/liveOps/loyalty/levels/slice";
import {
  getInfoRewardTitle,
  LOYALTY_REWARD_TYPES,
  LoyaltyLevelReward,
  LoyaltyRewardTranslation,
  LoyaltyRewardType,
  replaceBonusRewards,
} from "store/liveOps/loyalty/levels/rewards";

import LoyaltyRewardEntitySelectorModal from "./rewards/LoyaltyRewardEntitySelectorModal";
import LoyaltyRewardInfoModal from "./rewards/LoyaltyRewardInfoModal";
import styles from "./rewards/LoyaltyProgramLevelsRewardsTab.module.scss";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors: () => void;
  isReadOnly: boolean;
}

type SelectorType = "BONUS" | "CASHBACK";

interface EntitySelectorState {
  type: SelectorType;
  rewardIndex?: number;
}

interface InfoEditorState {
  rewardIndex?: number;
}

const LoyaltyProgramLevelsRewardsTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  resetErrors,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store["loyalty-levels"].payload);
  const locales = useAppSelector((store) => store.settings.locales);
  const rewards = payload?.rewards ?? [];
  const hasRewardsErrors = Object.keys(fieldErrors ?? {}).some((field) =>
    field.replace(/\[(\d+)\]/g, ".$1").startsWith("rewards."),
  );
  const [selector, setSelector] = useState<EntitySelectorState | null>(null);
  const [infoEditor, setInfoEditor] = useState<InfoEditorState | null>(null);
  const [selectorItems, setSelectorItems] = useState<
    Record<SelectorType, LoyaltyRewardSelectorItem[]>
  >({
    BONUS: [],
    CASHBACK: [],
  });
  const [selectorLoading, setSelectorLoading] = useState<
    Record<SelectorType, boolean>
  >({
    BONUS: false,
    CASHBACK: false,
  });
  const [selectorLoaded, setSelectorLoaded] = useState<
    Record<SelectorType, boolean>
  >({
    BONUS: false,
    CASHBACK: false,
  });
  const [selectorError, setSelectorError] = useState<
    Record<SelectorType, boolean>
  >({
    BONUS: false,
    CASHBACK: false,
  });

  const loadSelectorItems = async (type: SelectorType) => {
    if (selectorLoading[type] || selectorLoaded[type]) return;

    setSelectorError((current) => ({ ...current, [type]: false }));
    setSelectorLoading((current) => ({ ...current, [type]: true }));
    try {
      const items =
        type === "BONUS"
          ? await getLoyaltyBonusSelectorItems()
          : await getActiveCashbackSelectorItems();
      setSelectorItems((current) => ({ ...current, [type]: items }));
      setSelectorLoaded((current) => ({ ...current, [type]: true }));
    } catch (error) {
      setSelectorError((current) => ({ ...current, [type]: true }));
      handleApiError({ error, t, dispatch });
    } finally {
      setSelectorLoading((current) => ({ ...current, [type]: false }));
    }
  };

  useEffect(() => {
    let isCurrent = true;

    const preload = async (type: SelectorType) => {
      setSelectorError((current) => ({ ...current, [type]: false }));
      setSelectorLoading((current) => ({ ...current, [type]: true }));
      try {
        const items =
          type === "BONUS"
            ? await getLoyaltyBonusSelectorItems()
            : await getActiveCashbackSelectorItems();
        if (!isCurrent) return;
        setSelectorItems((current) => ({ ...current, [type]: items }));
        setSelectorLoaded((current) => ({ ...current, [type]: true }));
      } catch (error) {
        if (isCurrent) {
          setSelectorError((current) => ({ ...current, [type]: true }));
          handleApiError({ error, t, dispatch });
        }
      } finally {
        if (isCurrent) {
          setSelectorLoading((current) => ({ ...current, [type]: false }));
        }
      }
    };

    preload("BONUS");
    preload("CASHBACK");

    return () => {
      isCurrent = false;
    };
  }, [dispatch, t]);

  const normalizedTargetItems = useMemo(
    () => ({
      BONUS: new Map(
        selectorItems.BONUS.map((item) => [String(item.id), item]),
      ),
      CASHBACK: new Map(
        selectorItems.CASHBACK.map((item) => [String(item.id), item]),
      ),
    }),
    [selectorItems],
  );
  const bonusRewardEntries = rewards
    .map((reward, index) => ({ reward, index }))
    .filter(({ reward }) => reward.type === "BONUS");
  const firstBonusIndex = bonusRewardEntries[0]?.index;
  const isInitialRewardsLoading =
    (!selectorLoaded.BONUS && !selectorError.BONUS) ||
    (!selectorLoaded.CASHBACK && !selectorError.CASHBACK);

  const clearRewardErrors = (index: number) => {
    const normalizedRoot = `rewards.${index}`;
    Object.keys(fieldErrors ?? {}).forEach((field) => {
      const normalizedField = field.replace(/\[(\d+)\]/g, ".$1");
      if (normalizedField.startsWith(normalizedRoot)) clearFieldError(field);
    });
  };

  const hasRewardError = (index: number) => {
    const normalizedRoot = `rewards.${index}`;

    return Object.keys(fieldErrors ?? {}).some((field) =>
      field.replace(/\[(\d+)\]/g, ".$1").startsWith(normalizedRoot),
    );
  };

  const updateRewards = (nextRewards: LoyaltyLevelReward[]) => {
    dispatch(
      handleUpdateFields({
        rewards: nextRewards.map((reward, index) => ({
          ...reward,
          position: index + 1,
        })),
      }),
    );
  };

  const addAmountReward = (type: "COINS" | "RANKING") => {
    resetErrors();
    updateRewards([
      ...rewards,
      {
        type,
        position: rewards.length + 1,
        targetReference: null,
        config: { amount: "0" },
      },
    ]);
  };

  const openEntitySelector = (type: SelectorType, rewardIndex?: number) => {
    loadSelectorItems(type);
    setSelector({ type, rewardIndex });
  };

  const saveEntitySelection = (references: string[]) => {
    if (!selector) return;

    if (selector.type === "BONUS") {
      resetErrors();
      updateRewards(replaceBonusRewards(rewards, references));
      setSelector(null);
      return;
    }

    const reference = references[0];
    if (!reference) return;
    const reward: LoyaltyLevelReward = {
      ...(selector.rewardIndex !== undefined
        ? rewards[selector.rewardIndex]
        : {}),
      type: selector.type,
      position:
        selector.rewardIndex === undefined
          ? rewards.length + 1
          : selector.rewardIndex + 1,
      targetReference: reference,
      config: null,
    };

    if (selector.rewardIndex === undefined) {
      updateRewards([...rewards, reward]);
    } else {
      clearRewardErrors(selector.rewardIndex);
      updateRewards(
        rewards.map((currentReward, index) =>
          index === selector.rewardIndex ? reward : currentReward,
        ),
      );
    }
    setSelector(null);
  };

  const saveInfoReward = (
    translations: LoyaltyRewardTranslation[],
    isAvailable: boolean,
  ) => {
    if (!infoEditor) return;

    const reward: LoyaltyLevelReward = {
      ...(infoEditor.rewardIndex !== undefined
        ? rewards[infoEditor.rewardIndex]
        : {}),
      type: "INFO",
      position:
        infoEditor.rewardIndex === undefined
          ? rewards.length + 1
          : infoEditor.rewardIndex + 1,
      targetReference: null,
      config: null,
      isAvailable,
      translations,
    };

    if (infoEditor.rewardIndex === undefined) {
      updateRewards([...rewards, reward]);
    } else {
      clearRewardErrors(infoEditor.rewardIndex);
      updateRewards(
        rewards.map((currentReward, index) =>
          index === infoEditor.rewardIndex ? reward : currentReward,
        ),
      );
    }
    setInfoEditor(null);
  };

  const removeReward = (index: number) => {
    resetErrors();
    updateRewards(rewards.filter((_, rewardIndex) => rewardIndex !== index));
  };

  const removeBonusRewards = () => {
    resetErrors();
    updateRewards(rewards.filter((reward) => reward.type !== "BONUS"));
  };

  const updateAmount = (index: number, amount: string) => {
    clearRewardErrors(index);
    updateRewards(
      rewards.map((reward, rewardIndex) =>
        rewardIndex === index
          ? {
              ...reward,
              config: { amount },
            }
          : reward,
      ),
    );
  };

  const renderRewardDetails = (reward: LoyaltyLevelReward, index: number) => {
    if (reward.type === "COINS" || reward.type === "RANKING") {
      const errorKeys = getFieldErrorKeys(
        fieldErrors,
        `rewards.${index}`,
        `rewards.${index}.config`,
        `rewards.${index}.config.amount`,
      );

      return (
        <div className={styles.amountInput}>
          <InnerInput
            value={reward.config?.amount ?? ""}
            placeholder={t("amount")}
            label={t("amount")}
            type="number"
            inputMode="numeric"
            min={1}
            id={`loyalty-reward-${reward.type.toLowerCase()}-${index}`}
            onChange={(amount) => updateAmount(index, amount)}
            hasError={errorKeys.length > 0}
            disabled={isReadOnly}
          />
        </div>
      );
    }

    if (reward.type === "INFO") {
      const available = reward.isAvailable !== false;
      return (
        <span className={styles.infoRewardSummary}>
          <span className={styles.rewardTitle}>
            {getInfoRewardTitle(reward, locales) || t("untitledInfoReward")}
          </span>
          <span className={`badge ${available ? "badge--success" : "badge--disabled"}`}>
            {available ? t("available") : t("unavailable")}
          </span>
        </span>
      );
    }

    const target = normalizedTargetItems[reward.type].get(
      reward.targetReference ?? "",
    );

    return (
      <span className={styles.rewardTitle}>
        {target?.title ??
          `${t(reward.type.toLowerCase())} #${reward.targetReference ?? "—"}`}
        <span className={styles.rewardMeta}>
          ID {reward.targetReference ?? "—"}
        </span>
      </span>
    );
  };

  const renderBonusGroupDetails = () => (
    <div className={styles.bonusTargets}>
      {bonusRewardEntries.map(({ reward, index }) => {
        const target = normalizedTargetItems.BONUS.get(
          reward.targetReference ?? "",
        );

        return (
          <span
            className="badge badge--info"
            key={`${reward.id ?? reward.targetReference}-${index}`}
          >
            {target?.title ?? `${t("bonus")} #${reward.targetReference ?? "—"}`}
          </span>
        );
      })}
    </div>
  );

  return (
    <>
      {isInitialRewardsLoading ? (
        <ModalTabSkeleton />
      ) : (
        <div className={styles.rewardsLayout}>
          <section className={styles.rewardsPanel}>
            <h3 className={styles.panelTitle}>
              {t("selectedRewards")} ({rewards.length})
            </h3>
            {hasRewardsErrors && (
              <p className={styles.validationMessage}>
                {t("fixRewardValidationErrors")}
              </p>
            )}

            {rewards.length === 0 ? (
              <EmptyState message={t("noLoyaltyRewards")} />
            ) : (
              <div className={styles.selectedRewards}>
                {rewards.map((reward, index) => {
                  const isBonusGroup =
                    reward.type === "BONUS" && index === firstBonusIndex;
                  if (reward.type === "BONUS" && !isBonusGroup) return null;
                  const hasError = isBonusGroup
                    ? bonusRewardEntries.some(({ index: bonusIndex }) =>
                        hasRewardError(bonusIndex),
                      )
                    : hasRewardError(index);
                  const canEdit =
                    isBonusGroup ||
                    reward.type === "CASHBACK" ||
                    reward.type === "INFO";

                  return (
                    <div
                      className={`${styles.rewardRow} ${
                        hasError ? styles.rewardRowError : ""
                      }`}
                      key={
                        isBonusGroup
                          ? "loyalty-bonus-group"
                          : `${reward.id ?? reward.type}-${index}`
                      }
                    >
                      <div className={styles.rewardIdentity}>
                        <img
                          className={styles.rewardIcon}
                          src={rewardIcon}
                          alt=""
                        />
                        <div className={styles.rewardCopy}>
                          <span className={styles.rewardType}>
                            {t(reward.type.toLowerCase())}
                          </span>
                          {isBonusGroup
                            ? renderBonusGroupDetails()
                            : renderRewardDetails(reward, index)}
                        </div>
                      </div>

                      {!isReadOnly && (
                        <div className={styles.rewardActions}>
                          <Options
                            id={isBonusGroup ? "BONUS" : index}
                            hasEdit={canEdit}
                            hasDelete
                            instantDelete
                            onEdit={() => {
                              if (isBonusGroup) {
                                openEntitySelector("BONUS");
                              } else if (reward.type === "CASHBACK") {
                                openEntitySelector("CASHBACK", index);
                              } else if (reward.type === "INFO") {
                                setInfoEditor({ rewardIndex: index });
                              }
                            }}
                            onDelete={() =>
                              isBonusGroup
                                ? removeBonusRewards()
                                : removeReward(index)
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className={styles.rewardsPanel}>
            <h3 className={styles.panelTitle}>{t("availableRewards")}</h3>
            <div className={styles.availableRewards}>
              {LOYALTY_REWARD_TYPES.map((type: LoyaltyRewardType) => (
                <div className={styles.availableRewardRow} key={type}>
                  <span>{t(type.toLowerCase())}</span>
                  <ActionBtn
                    txt={t("add")}
                    tooltip={t("add")}
                    disabled={isReadOnly}
                    handleClick={() => {
                      if (type === "BONUS" || type === "CASHBACK") {
                        openEntitySelector(type);
                      } else if (type === "INFO") {
                        setInfoEditor({});
                      } else {
                        addAmountReward(type);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {selector && (
        <LoyaltyRewardEntitySelectorModal
          type={selector.type}
          items={selectorItems[selector.type]}
          isLoading={selectorLoading[selector.type]}
          hasError={selectorError[selector.type]}
          selectedReferences={
            selector.type === "BONUS"
              ? Array.from(
                  new Set(
                    rewards
                      .filter((reward) => reward.type === "BONUS")
                      .map((reward) => reward.targetReference)
                      .filter((reference): reference is string =>
                        Boolean(reference),
                      ),
                  ),
                )
              : selector.rewardIndex === undefined
                ? []
                : [rewards[selector.rewardIndex]?.targetReference].filter(
                    (reference): reference is string => Boolean(reference),
                  )
          }
          onClose={() => setSelector(null)}
          onSave={saveEntitySelection}
          onRetry={() => loadSelectorItems(selector.type)}
        />
      )}

      {infoEditor && (
        <LoyaltyRewardInfoModal
          locales={locales}
          initialTranslations={
            infoEditor.rewardIndex === undefined
              ? []
              : (rewards[infoEditor.rewardIndex]?.translations ?? [])
          }
          initialIsAvailable={
            infoEditor.rewardIndex === undefined
              ? true
              : (rewards[infoEditor.rewardIndex]?.isAvailable ??
                  rewards[infoEditor.rewardIndex]?.config?.isAvailable) !== false
          }
          onClose={() => setInfoEditor(null)}
          onSave={saveInfoReward}
        />
      )}
    </>
  );
};

export default LoyaltyProgramLevelsRewardsTab;
